import React from 'react'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteSharpIcon from '@mui/icons-material/DeleteSharp';

import Tooltip from "@mui/material/Tooltip";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import classnames from "classnames";
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EditLocationIcon from '@mui/icons-material/EditLocation';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import {Link} from "react-router-dom";
import {buildLocationString} from "../../utils/utils";
import {usePricingRow} from "./usePricingRow";
import {PriceAdjustSignToggle} from "./PriceAdjustSignToggle";


export const PricingListRow = ( { trim, pricing } ) => {
    const {
        pricingState,
        updatePricingState,
        priceAdjust,
        setPriceAdjustSign,
        setPriceAdjustMagnitude,
        msrpPercentage,
        setMsrpPercentage,
        dealLocation,
        isDefaultLocation,
        deletePricingRow,
        isDeleting,
        deskPricing,
        openLocationModal,
        buildId,
        isLoading,
        isDisabled,
        isCreating,
        isCreatingLease,
        isCreatingPurchase,
    } = usePricingRow({trim, pricing})

    return (
        <React.Fragment
            key={pricing?.id || `${trim.id}-pricing-new`}
        >
            {
                pricing?.id &&
                <td className={'delete-pricing-row-icon'}>
                    <Tooltip title="Delete" arrow placement={'top'}>
                        <DeleteSharpIcon
                            className={classnames(
                                'action-icon garbage', {
                                    loader: isDeleting
                                }
                            )}
                            onClick={() => deletePricingRow(pricing.id)}
                        />
                    </Tooltip>
                </td>
            }
            <td className={'pricing-list-row_description'}>
                <input
                    className={'description-input'}
                    placeholder={'Add notes'}
                    value={pricingState.description || ''}
                    onChange={(e) => updatePricingState('description', e.target.value)}
                />
            </td>
            <td>
                <div className={'pricing-money-wrap'}>
                    <span className={'prefix'}>$</span>
                    <CustomCurrencyInput
                        id={buildId('msrp')}
                        value={pricingState.msrp}
                        onChange={(value) => updatePricingState('msrp', value)}
                        placeholder={'0'}
                        className={classnames('payment-row_value', {
                            loading: isCreating
                        })}
                        InputProps={{}}
                    />
                </div>
            </td>
            <td>
                <div className={'pricing-percent-control'}>
                    {/* Direction is fixed — the column is "% OFF MSRP" — so this
                        is a static label, not a toggle like Price Adjust's. Sits
                        outside the box, matching the Cost Calculator's "− %" row. */}
                    <span className={'pricing-percent-op'}>−</span>
                    <div className={'pricing-percent-wrap'}>
                        <CustomCurrencyInput
                            id={buildId('msrp_percentage')}
                            value={msrpPercentage}
                            onChange={setMsrpPercentage}
                            // Blank, not 0 — a row priced at MSRP has no "% off",
                            // and a box showing 0 turns a typed 5 into '05'.
                            allowBlank={true}
                            allowNegative={false}
                            placeholder={'0'}
                            className={classnames('payment-row_value', {
                                loading: isCreating
                            })}
                            InputProps={{}}
                        />
                        <span className={'suffix'}>%</span>
                    </div>
                </div>
            </td>
            <td>
                <div className={'pricing-money-wrap'}>
                    <span className={'prefix'}>$</span>
                    <CustomCurrencyInput
                        id={buildId('invoice')}
                        value={pricingState.invoice}
                        onChange={(value) => updatePricingState('invoice', value)}
                        allowNull={true}
                        className={classnames('payment-row_value', {
                            loading: isCreating
                        })}
                        InputProps={{}}
                    />
                </div>
            </td>
            <td>
                <div className={'price-adjust-control'}>
                    <PriceAdjustSignToggle
                        sign={priceAdjust.sign}
                        onChange={setPriceAdjustSign}
                        buildId={buildId}
                    />
                    <div className={'pricing-money-wrap'}>
                        <span className={'prefix'}>$</span>
                        <CustomCurrencyInput
                            id={buildId('price_adjust')}
                            value={priceAdjust.magnitude}
                            // Blank, not a literal 0 — see getPriceAdjustState.
                            allowBlank={true}
                            allowNegative={false}
                            placeholder={'0'}
                            // Straight through, so the handler still sees the
                            // sourceInfo that marks a re-render from a keystroke.
                            onChange={setPriceAdjustMagnitude}
                            className={classnames('payment-row_value', {
                                loading: isCreating
                            })}
                            InputProps={{}}
                        />
                    </div>
                </div>
            </td>
            <td>
                <div className={'pricing-money-wrap'}>
                    <span className={'prefix'}>$</span>
                    <CustomCurrencyInput
                        id={buildId('profit')}
                        value={pricingState.profit}
                        defaultValue={0}
                        onChange={(value) => updatePricingState('profit', value)}
                        className={classnames('payment-row_value', {
                            loading: isCreating
                        })}
                        InputProps={{}}
                    />
                </div>
            </td>
            <td>
                <Tooltip title={'Lease'} arrow placement={'top'}>
                    <AddOutlinedIcon
                        className={classnames(
                            'action-icon', {
                                loader: isCreatingLease,
                                disabled: isDisabled
                            }
                        )}
                        onClick={() => {
                            if (isLoading) return;
                            deskPricing(false)
                        }}
                    />
                </Tooltip>
                <Tooltip title={'Purchase'} arrow placement={'top'}>
                    <AccountBalanceIcon
                        className={classnames(
                            'action-icon purchase', {
                                loader: isCreatingPurchase,
                                disabled: isDisabled
                            }
                        )}
                        onClick={() => {
                            if (isLoading) return;
                            deskPricing(true)
                        }}
                    />
                </Tooltip>

                <Tooltip
                    title={`Edit Location (${buildLocationString(dealLocation)})`}
                    arrow placement={'top'}
                >
                    {
                        isDefaultLocation ?
                            <LocationOnIcon
                                className={'action-icon'}
                                onClick={openLocationModal}
                            /> :
                            <EditLocationIcon
                                className={'action-icon'}
                                onClick={openLocationModal}
                            />
                    }
                </Tooltip>
                {
                    // Existing Pricing Section
                    pricing?.id &&
                    <Tooltip title={'Quotes'} arrow placement={'top'}>
                        <Link to={`/quotes?pricing_id=${pricing.id}`} target="_blank">
                            <LibraryBooksIcon
                                className={'action-icon'}
                            />
                        </Link>
                    </Tooltip>

                }
            </td>
        </React.Fragment>
    )
}
